import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import RecommendationTip from '../components/RecommendationTip';
import '../styles/EnterpriseDevelopmentScorecard.scss';
import { themeVars } from '../config/theme';

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
const fmtCurrency = (n) => `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const rowClass = (actual, target) => {
  if (actual === null || actual === 0) return 'sc-row--empty';
  return actual >= target ? 'sc-row--achieved' : 'sc-row--partial';
};

// Per-element display metadata. Adding a new sector's socio-economic development
// variant means adding one entry here, not a new page file.
const SCORECARD_META = {
  sed: {
    title: 'Socio-Economic Development Scorecard',
    subtitle: 'B-BBEE — SED Element Calculation',
    backPath: '/socio-economic-development',
    docPath: '/sed/submit-datasheet',
    docSheetName: 'SED Data Sheet',
    summaryLabel: 'Total SED Points',
  },
  sed_transport: {
    title: 'Socio-Economic Development Scorecard (Transport)',
    subtitle: 'B-BBEE — SED Element Calculation (Transport Road Freight)',
    backPath: '/socio-economic-development',
    docPath: '/sed/submit-datasheet/sed-transport',
    docSheetName: 'Road Freight SED Data Sheet',
    summaryLabel: 'Total SED Points',
  },
};

const SEDScorecard = () => {
  const navigate = useNavigate();
  const { elementKey: rawElementKey } = useParams();
  const elementKey = (rawElementKey || 'sed').replace(/-/g, '_');
  const meta = SCORECARD_META[elementKey] || SCORECARD_META.sed;

  const [indicators, setIndicators] = useState([]);
  const [results, setResults] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [filename, setFilename] = useState(null);
  const [spendSummary, setSpendSummary] = useState(null);
  const [npat, setNpat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [noDataSheet, setNoDataSheet] = useState(false);
  const [noNPAT, setNoNPAT] = useState(false);
  const [negativeNPATModal, setNegativeNPATModal] = useState(false);

  const applyData = (data) => {
    setIndicators(data.indicators || []);
    setResults(data.results);
    setTotalPoints(data.total_points);
    setFilename(data.filename);
    setSpendSummary(data.sed_spend_summary ?? null);
    setNpat(data.npat ?? null);
    setLoading(false);
    setCalculating(false);
    setError(null);
    setNoDataSheet(false);
    setNoNPAT(false);
  };

  const recalculate = async () => {
    setCalculating(true);
    setError(null);
    setNoDataSheet(false);
    setNoNPAT(false);
    try {
      const res = await api.post('/core/scorecard/calculate_from_data_sheet/', { element: elementKey });
      applyData(res.data);
    } catch (err) {
      setCalculating(false);
      setResults(null);
      setTotalPoints(0);
      setSpendSummary(null);
      setFilename(null);
      setNpat(null);
      if (err.response?.status === 404) setNoDataSheet(true);
      else if (err.response?.status === 422 && err.response?.data?.error === 'negative_npat') setNegativeNPATModal(true);
      else if (err.response?.status === 422) setNoNPAT(true);
      else setError(err.response?.data?.error || 'Failed to recalculate SED.');
    }
  };

  useEffect(() => {
    api.get(`/core/scorecard/get_scorecard/?element=${elementKey}`)
      .then(res => applyData(res.data))
      .catch(err => {
        setLoading(false);
        if (err.response?.data?.indicators?.length) setIndicators(err.response.data.indicators);
        if (err.response?.status === 422 && err.response?.data?.error === 'negative_npat') setNegativeNPATModal(true);
        else if (err.response?.status === 404) recalculate();
      });
  }, [elementKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToDoc = () => navigate(meta.docPath);

  const resultMap = results ? Object.fromEntries(results.map((r) => [r.id, r])) : {};
  const maxPoints = indicators.reduce((s, ind) => s + (ind.points || 0), 0);

  return (
    <div className="sc-container themed" style={themeVars(elementKey)}>
      <div className="sc-header">
        <button className="sc-back" onClick={() => navigate(meta.backPath)}>← Back</button>
        <div>
          <h1>{meta.title}</h1>
          <p>{meta.subtitle}</p>
        </div>
      </div>

      {negativeNPATModal && (
        <div className="sc-modal-overlay">
          <div className="sc-modal">
            <h3 className="sc-modal-title">⚠ Negative NPAT</h3>
            <p className="sc-modal-body">
              Your current NPAT is negative. Targets will be based on the Indicative or Industry Norm.
            </p>
            <div className="sc-modal-actions">
              <button className="sc-modal-btn sc-modal-btn--primary" onClick={() => navigate('/finance-statutory/npat')}>
                Go to NPAT
              </button>
              <button className="sc-modal-btn sc-modal-btn--secondary" onClick={() => setNegativeNPATModal(false)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sc-upload-bar">
        <div className="sc-upload-info">
          {loading
            ? <span className="sc-upload-hint">Loading…</span>
            : filename
              ? <span className="sc-upload-name">✓ Calculated from: <strong>{filename}</strong></span>
              : <span className="sc-upload-hint">No data sheet found</span>
          }
        </div>
        <div className="sc-upload-actions">
          <button className="sc-template-btn" onClick={goToDoc}>Go to Documentation</button>
          <button className="sc-upload-btn" onClick={recalculate} disabled={calculating || loading}>
            {calculating ? 'Calculating…' : 'Recalculate'}
          </button>
        </div>
      </div>

      {noDataSheet && (
        <div className="sc-error">
          No data sheet uploaded yet. Go to{' '}
          <button className="sc-error-link" onClick={goToDoc}>Documentation</button>{' '}
          and upload the <strong>{meta.docSheetName}</strong>.
        </div>
      )}
      {noNPAT && (
        <div className="sc-error">
          No NPAT found. Please update your NPAT in{' '}
          <button className="sc-error-link" onClick={() => navigate('/finance-statutory/company-information')}>Company Information</button>.
        </div>
      )}
      {error && <div className="sc-error">{error}</div>}

      {spendSummary && spendSummary.length > 0 && (
        <div className="sc-spend-table-outer">
          <h2 className="sc-spend-title">NPAT &amp; Spend Summary</h2>
          <div className="sc-table-wrapper">
            <table className="sc-table sc-spend-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>NPAT Used</th>
                  <th>Target</th>
                  <th>Actual Spend</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {spendSummary.map((row) => {
                  const diff = row.spend_diff;
                  return (
                    <tr key={row.category} className={diff > 0 ? 'sc-row--achieved' : 'sc-row--partial'}>
                      <td>{row.category}</td>
                      <td>{npat !== null ? fmtCurrency(npat) : '—'}</td>
                      <td>{fmtCurrency(row.target_spend)}</td>
                      <td>{fmtCurrency(row.spend)}</td>
                      <td>
                        <span className={diff > 0 ? 'sc-spend-diff sc-spend-diff--pos' : 'sc-spend-diff sc-spend-diff--neg'}>
                          {diff > 0 ? '+' : ''}{fmtCurrency(diff)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sc-table-outer">
        <div className="sc-table-wrapper">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Compliance Target</th>
                <th>Max Points</th>
                <th>Actual (%)</th>
                <th>Points Achieved</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((ind) => {
                const r = resultMap[ind.id];
                const actual = r?.actual ?? null;
                const pts = r?.points ?? 0;
                const isMax = pts >= ind.points;
                return (
                  <tr key={ind.id} className={rowClass(actual, ind.target)}>
                    <td>{ind.label}</td>
                    <td>{ind.targetLabel ?? `${ind.target}%`} of NPAT</td>
                    <td>{ind.points}</td>
                    <td>{actual !== null ? `${fmt(actual)}%` : '—'}</td>
                    <td>
                      <span className={`sc-points${isMax ? ' sc-points--max' : pts === 0 ? ' sc-points--zero' : ''}`}>
                        {fmt(pts)}
                      </span>
                      <RecommendationTip
                        ind={ind}
                        actual={actual}
                        pts={pts}
                        spendRow={spendSummary?.find(r => r.program === ind.id)}
                      />
                    </td>
                  </tr>
                );
              })}
              <tr className="sc-total-row">
                <td colSpan="3">Total</td>
                <td />
                <td>{fmt(totalPoints)} / {maxPoints || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="sc-summary">
        <div className="sc-summary-card sc-summary-card--total">
          <div className="sc-summary-score">{fmt(totalPoints)} <span>/ {maxPoints || '—'}</span></div>
          <div className="sc-summary-label">{meta.summaryLabel}</div>
        </div>
      </div>
    </div>
  );
};

export default SEDScorecard;
